import { Spacing } from '@persona-id/design-tokens';
import { sortBy } from 'lodash/fp';
import { ChangeEvent, useCallback } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Input from 'design-system/components/Input';
import { CountryCode, US_STATES } from 'lib/constants/locales';
import FormControlProps from 'lib/interfaces/FormControlProps';

const US_STATE_OPTIONS = sortBy(
  'label',
  Object.keys(US_STATES).map((code: string) => ({
    label: code,
    value: US_STATES[code as keyof typeof US_STATES],
  })),
);

interface GridProps {
  gridColumnSpan?: number;
  maxColumnSpan: 1 | 2 | 4;
}

const StyledFormSection = styled(FormSection)`
  grid-row-gap: ${Spacing[4]};
`;

const StyledInput = styled(Input)<GridProps>`
  grid-column: span
    ${({ gridColumnSpan, maxColumnSpan }) => Math.min(gridColumnSpan || 1, maxColumnSpan)};
`;

const StyledSelect = styled(ReactSelect)<GridProps>`
  grid-column: span
    ${({ gridColumnSpan, maxColumnSpan }) => Math.min(gridColumnSpan || 1, maxColumnSpan)};
`;

const SubdivisionInput = ({
  countryCode,
  onChange,
  ...otherProps
}: {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  countryCode?: CountryCode;
  hasError?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
} & GridProps) => {
  const handleInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const newValue = evt.currentTarget.value;
      onChange?.(newValue);
    },
    [onChange],
  );

  if (countryCode === 'US') {
    return (
      <StyledSelect
        {...(otherProps as any)}
        placeholder='State'
        options={US_STATE_OPTIONS}
        onChange={onChange}
        portal={true}
      />
    );
  }

  return (
    <StyledInput
      {...(otherProps as any)}
      type='text'
      placeholder='Province'
      onChange={handleInputChange}
    />
  );
};

const LocalizedAddressInput = ({
  countryCode,
  renderStreet1Input,
  renderStreet2Input,
  renderCityInput,
  renderSubdivisionInput,
  renderPostalCodeInput,
}: {
  countryCode: CountryCode;
  renderStreet1Input: (gridColumnSpan: number) => JSX.Element;
  renderStreet2Input: (gridColumnSpan: number) => JSX.Element;
  renderCityInput: (gridColumnSpan: number) => JSX.Element;
  renderSubdivisionInput: (gridColumnSpan: number) => JSX.Element;
  renderPostalCodeInput: (gridColumnSpan: number) => JSX.Element;
}) => {
  // Address formats are from http://bitboost.com/ref/international-address-formats.html

  switch (countryCode) {
    case 'AR':
    case 'GL':
    case 'IS':
      return (
        <>
          {renderStreet1Input(4)}
          {renderStreet2Input(4)}
          {renderCityInput(2)} {renderPostalCodeInput(2)}
        </>
      );

    case 'AT':
    case 'BE':
    case 'DK':
      return (
        <>
          {renderStreet1Input(2)} {renderStreet2Input(2)}
          {renderPostalCodeInput(1)} {renderCityInput(3)}
        </>
      );

    case 'BR':
    case 'CR':
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderCityInput(2)} {renderSubdivisionInput(2)}
          {renderPostalCodeInput(4)}
        </>
      );

    case 'CA':
      return (
        <>
          {renderStreet2Input(1)} {renderStreet1Input(3)}
          {renderCityInput(2)} {renderSubdivisionInput(1)} {renderPostalCodeInput(1)}
        </>
      );

    case 'CN':
      return (
        <>
          {renderStreet2Input(4)}
          {renderStreet1Input(2)} {renderCityInput(2)}
          {renderSubdivisionInput(2)} {renderPostalCodeInput(2)}
        </>
      );

    case 'IT':
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderPostalCodeInput(1)} {renderCityInput(2)} {renderSubdivisionInput(1)}
        </>
      );

    // Note that while GB is postal code primary, we still render it as the
    // last field after autocompletion. This is what Amazon UK does.
    case 'AU':
    case 'GB':
    case 'PH':
    case 'US':
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderCityInput(2)} {renderSubdivisionInput(1)} {renderPostalCodeInput(1)}
        </>
      );

    case 'FI':
    case 'FR':
    case 'DE':
    case 'IL':
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderPostalCodeInput(1)} {renderCityInput(3)}
        </>
      );

    case 'JP':
    case 'KR':
      // CURRENTLY, this is the same as Default...?
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderCityInput(3)} {renderPostalCodeInput(1)}
        </>
      );

    case 'IN':
      return (
        <>
          {renderStreet2Input(4)}
          {renderStreet1Input(4)}
          {renderCityInput(3)} {renderPostalCodeInput(1)}
        </>
      );

    case 'FJ':
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderCityInput(4)}
        </>
      );

    case 'HK':
      return (
        <>
          {renderStreet2Input(4)}
          {renderStreet1Input(4)}
          {renderCityInput(4)}
        </>
      );

    case 'EE':
    case 'CZ':
    case 'TW':
    case 'ID':
    default:
      return (
        <>
          {renderStreet1Input(3)} {renderStreet2Input(1)}
          {renderCityInput(3)} {renderPostalCodeInput(1)}
        </>
      );
  }
};

export interface Address {
  street1?: string;
  street2?: string;
  city?: string;
  subdivision?: string;
  postalCode?: string;
}

export interface AddressInputProps extends FormControlProps {
  id?: string;
  className?: string;
  name?: string;
  disabled?: boolean;
  cols?: 1 | 2 | 4;
  countryCode?: CountryCode;
  value?: Address;
  onChange?: (value: Address) => void;
}

const AddressInput = ({
  id,
  className,
  name,
  value,
  disabled,
  cols = 4,
  countryCode = 'US',
  hasError,
  onChange,
}: AddressInputProps) => {
  const handleStreet1Change = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const street1 = evt.currentTarget.value;
      onChange?.({ ...value, street1 });
    },
    [onChange],
  );

  const handleStreet2Change = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const street2 = evt.currentTarget.value;
      onChange?.({ ...value, street2 });
    },
    [onChange],
  );

  const handleCityChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const city = evt.currentTarget.value;
      onChange?.({ ...value, city });
    },
    [onChange],
  );

  const handleSubdivisionChange = useCallback(
    (subdivision: string) => {
      onChange?.({ ...value, subdivision });
    },
    [onChange],
  );

  const handlePostalCodeChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const postalCode = evt.currentTarget.value;
      onChange?.({ ...value, postalCode });
    },
    [onChange],
  );

  const renderStreet1Input = (gridColumnSpan: number) => (
    <StyledInput
      id={`${id}--street-1`}
      name={`${name}-street-1`}
      data-test={`${id}-street-1`}
      value={value?.street1}
      type='text'
      placeholder='Street address'
      disabled={disabled}
      hasError={hasError}
      onChange={handleStreet1Change}
      gridColumnSpan={gridColumnSpan}
      maxColumnSpan={cols}
    />
  );

  const renderStreet2Input = (gridColumnSpan: number) => (
    <StyledInput
      id={`${id}--street-2`}
      name={`${name}-street-2`}
      data-test={`${id}-street-2`}
      value={value?.street2}
      type='text'
      placeholder='Apt / Suite'
      disabled={disabled}
      hasError={hasError}
      onChange={handleStreet2Change}
      gridColumnSpan={gridColumnSpan}
      maxColumnSpan={cols}
    />
  );

  const renderCityInput = (gridColumnSpan: number) => (
    <StyledInput
      id={`${id}--city`}
      name={`${name}-city`}
      data-test={`${id}-city`}
      value={value?.city}
      type='text'
      placeholder='City'
      disabled={disabled}
      hasError={hasError}
      onChange={handleCityChange}
      gridColumnSpan={gridColumnSpan}
      maxColumnSpan={cols}
    />
  );

  const renderSubdivisionInput = (gridColumnSpan: number) => (
    <SubdivisionInput
      id={`${id}--subdivision`}
      name={`${name}-subdivision`}
      data-test={`${id}-subdivision`}
      value={value?.subdivision}
      disabled={disabled}
      hasError={hasError}
      onChange={handleSubdivisionChange}
      countryCode={countryCode}
      gridColumnSpan={gridColumnSpan}
      maxColumnSpan={cols}
    />
  );

  const renderPostalCodeInput = (gridColumnSpan: number) => (
    <StyledInput
      id={`${id}--postal-code`}
      name={`${name}-postal-code`}
      data-test={`${id}-postal-code`}
      value={value?.postalCode}
      type='text'
      placeholder='Postal code'
      disabled={disabled}
      hasError={hasError}
      onChange={handlePostalCodeChange}
      gridColumnSpan={gridColumnSpan}
      maxColumnSpan={cols}
    />
  );

  return (
    <StyledFormSection cols={cols} className={className}>
      <LocalizedAddressInput
        countryCode={countryCode}
        renderStreet1Input={renderStreet1Input}
        renderStreet2Input={renderStreet2Input}
        renderCityInput={renderCityInput}
        renderSubdivisionInput={renderSubdivisionInput}
        renderPostalCodeInput={renderPostalCodeInput}
      />
    </StyledFormSection>
  );
};

export default AddressInput;
